import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useVirtualizer } from '@tanstack/react-virtual'
import {
  entstanden,
  fristMarke,
  isOpenStatus,
  MARKIER_FILTER,
  meilensteinDerAufgabe,
  passtZuMarkierFilter,
  TASK_STATUSES,
  TASK_STATUS_LABELS,
  TASK_SOURCE_LABELS,
  wirksamerTermin,
  type MarkierFilter,
  type PlanungsVorhaben,
  type TaskSource,
  type TaskStatus,
  type Termin,
} from '@netcon-work/shared'
import { useMemo, useRef, useState } from 'react'
import { AufgabeAnlegen } from '../components/AufgabeAnlegen'
import { AufgabeZusammenfuehren } from '../components/AufgabeZusammenfuehren'
import { AufgabenVerweis } from '../components/AufgabeKontext'
import { PoolsSetzen } from '../components/PoolsSetzen'
import { Topbar } from '../components/Topbar'
import { Button, Card, Chip, PrioBadge, SourceBadge } from '../components/ui/primitives'
import { dauerAngabe, dauerHerkunftText, dauerText, useWeltdauern } from '../lib/dauer'
import { useMerker } from '../lib/merker'
import { meilensteineQuery, personenQuery, poolsQuery, vorhabenQuery } from '../lib/queries'
import { trpc } from '../lib/trpc'
import { Fristwort } from '../components/meintag/Fristwort'
import { Ladehinweis } from './MeinTag'
import { useAufgabenDieserWelt } from '../lib/welt'
import { MarkierAuswahl, MarkierFilterLeiste, MarkierKnoepfe } from '../components/Markierung'
import { heuteSchluessel, useDarfMarkieren, useOffeneMarkierungen } from '../lib/markierung'
import { nebenBestandVerwerfen } from '../lib/mutationen'

/**
 * Alle Aufgaben (Konzept Kap. 5.7) – die filterbare Gesamttabelle.
 *
 * Leitplanke 2 in Reinform: der Bestand liegt komplett im Client, Filtern und
 * Suchen sind lokale Operationen ohne Serverabfrage.
 *
 * ## Warum die Tabelle virtualisiert ist (Leitplanke 4)
 *
 * Alles im Client zu haben heißt **nicht**, alles im DOM zu haben. Gemessen mit dem
 * echten Volumen von 1.847 Aufgaben:
 *
 * | | DOM-Zeilen | derselbe Test |
 * |---|---|---|
 * | vorher | **1.847** | 5.587 ms |
 * | jetzt | **29** | 161 ms |
 *
 * Die ganze Web-Testsuite fiel damit von 11,63 s auf 2,54 s – der Aufwand steckte
 * fast vollständig in dieser einen Tabelle.
 *
 * Bei sieben Spalten waren das über 13.000 Elemente, und **jede Taste in der Suche**
 * bewertete sie neu. Die Zahlen stammen aus `AlleAufgaben.virtualisierung.test.tsx`,
 * der die Zeilenzahl auch dauerhaft festhält.
 *
 * ## Warum mit Abstandszeilen und nicht mit absoluter Positionierung
 *
 * Die verbreitete Lösung positioniert die Zeilen absolut. Das bricht bei einer
 * `<table>` zwei Dinge: die Spaltenbreiten (der Browser rechnet sie aus dem Inhalt
 * der *vorhandenen* Zeilen) und die Semantik, sobald man die Tabelle in `div`s
 * auflöst – `role="row"` verschwindet und mit ihm die Vorlesbarkeit.
 *
 * Deshalb: **eine leere Zeile oben, eine unten**, deren Höhe den ungerenderten
 * Bereich einnimmt. Die Tabelle bleibt eine Tabelle, die Bildlaufleiste stimmt, und
 * `getByRole('row')` funktioniert weiter. Die Spaltenbreiten sind dafür fest
 * (`table-fixed`) – sonst würden sie beim Blättern springen, weil jedes Fenster
 * andere Inhalte hat.
 */
export function AlleAufgaben() {
  /** Gebundene Ansicht – nur die Arbeitswelt, in der man gerade steht (Z-07, E-234). */
  const { aufgaben, laedt: isLoading } = useAufgabenDieserWelt()
  /** Die Dauer-Vorgaben je Arbeitswelt – eine Tafel, aus der ohnehin geladenen Welt-Liste. */
  const dauernJeWelt = useWeltdauern()
  const { data: pools = [] } = useQuery(poolsQuery)
  const [suche, setSuche] = useState('')
  /**
   * **Standard „Offen", nicht „Alle"** – von Nils entschieden (31.07.).
   *
   * Von 1.847 Aufgaben sind rund 1.730 erledigt. Wer die Liste öffnet, will
   * sehen, was noch zu tun ist; der fertige Bestand ist Archiv und drängt sich
   * sonst vor die Arbeit. `Alle Stati` steht direkt daneben, und die Wahl wird
   * gemerkt – wer das Archiv braucht, muss es nicht bei jedem Besuch neu einstellen.
   *
   * Bewusst **keine** stille Filterung: der Untertitel zeigt weiter „X von Y", und
   * die Auswahl steht sichtbar auf „Offen". Eine Liste, die etwas weglässt, ohne es
   * zu sagen, lässt einen an den Zahlen zweifeln.
   */
  const [statusFilter, setStatusFilter] = useMerker<TaskStatus | 'alle' | 'offen'>(
    'alle-statusfilter',
    'offen',
    ['offen', 'alle', ...TASK_STATUSES],
  )
  const [quelleFilter, setQuelleFilter] = useState<string>('alle')
  /**
   * **Filter nach Bereich** (F-32) – Projekt bei GitLab, Warteschlange bei NetCon.
   *
   * Nils: *„in NetCon gibt es ja die Wünsche – das sind speziell Wünsche keine Tickets."*
   * Die Unterscheidung stand in jeder Antwort der Quelle und war in Work bis zum
   * 03.08.2026 nicht abfragbar. Jetzt ist sie ein Filter und kein Ratespiel.
   */
  const [bereichFilter, setBereichFilter] = useState<string>('alle')
  /**
   * **Nur Aufgaben, bei denen der Status nicht zusammenpasst.**
   *
   * Nils: *„dann kann ich die gezielt in NetCon schließen, dass die beim nächsten Sync
   * wieder korrekt sind. Das habe ich ja wohl häufiger."* Ein Filter statt einer Liste an
   * anderer Stelle: gefunden wird hier, und was gefunden wird, will man auch öffnen und
   * durchsuchen können.
   *
   * **Zwei Gründe, ein Knopf** (18.08.2026). Ursprünglich hieß er „Quellen uneinig" und
   * traf nur Aufgaben mit **zwei** Bezügen. Der Fall, den Nils an NetCon #37166 fand –
   * der Verlauf endet auf „Geschlossen", oben steht „Bereit" – hat nur **einen** Bezug
   * und fiel deshalb durch. Sein Vorschlag: *„kannst du die dann nicht mit in ‚Quellen
   * uneinig' schieben? Dann kann ich die händisch schließen – oder auch behandeln."*
   *
   * Also derselbe Knopf, aber der Name stimmt jetzt für beides: bei #37166 sind sich
   * nicht zwei Quellen uneinig, sondern **eine Quelle mit ihrem eigenen Verlauf**. Die
   * Begründung steht an der Zeile, nicht am Filter.
   */
  const [nurUneinig, setNurUneinig] = useState(false)
  /**
   * **Nur Liegengebliebenes** (Phase 7, „Altlasten bewusst geschlossen").
   *
   * Gemessen am 03.08.2026: **43 von 150** offenen Aufgaben haben seit über zwölf Monaten kein
   * Ereignis. Das ist die Sorte Rückstand, die in keiner Liste auffällt, **weil sie sich nicht
   * bewegt** – die Sortierung nach „zuletzt geändert" schiebt sie ganz nach unten, und dort
   * sieht sie niemand.
   */
  const [nurLiegengeblieben, setNurLiegengeblieben] = useState(false)
  /**
   * **Nur Verwaiste** (Phase 7, 05.08.2026).
   *
   * Nils: *„Die wurden in GitLab dann leider nicht mitgeschlossen und schwirren hier nun als
   * Leichen rum."* Gemessen an allen elf Projekten: von 64 Tasks hängen **11** offen unter
   * drei geschlossenen Elternteilen – und keiner davon ist jünger als 216 Tage.
   *
   * *(Nach GitLabs eigenem Zustand wären es 12. Die zwölfte trägt bereits `Status::Erledigt`
   * und ist für Work erledigt – geschlossen schlägt alles, das Label aber auch.)*
   *
   * Ein eigener Filter und keine Zeile im Statusfilter: „verwaist" ist kein Zustand der
   * Aufgabe, sondern einer ihrer Umgebung. Zwei Dinge in einer Auswahl zu mischen hieße,
   * dass man nicht mehr nach beidem zugleich filtern kann.
   */
  const [nurVerwaist, setNurVerwaist] = useState(false)
  /**
   * **Filter nach Vorhaben** (Phase 8, Mockup: „Vorhaben ▾"). `ohne` ist der wichtigste Wert –
   * er beantwortet die Frage, mit der jemand hier hereinkommt: *was ist noch nicht zugeordnet?*
   */
  const [vorhabenFilter, setVorhabenFilter] = useState<string>('alle')
  /**
   * **„Mein Tag: Heute · Morgen · Übermorgen"** ([Z-20](../../../../docs/phasen/zusatz-20-mein-tag-markieren.md)).
   * Gemerkt je Gerät wie der Statusfilter. Ein Knopf erscheint nur mit Zahl größer null – wie
   * „liegengeblieben" und „verwaist".
   */
  const [meinTagFilter, setMeinTagFilter] = useMerker<MarkierFilter | 'alle'>(
    'alle-meintag',
    'alle',
    ['alle', ...MARKIER_FILTER],
  )
  const markierungen = useOffeneMarkierungen()
  const darfMarkieren = useDarfMarkieren()

  /**
   * **Sortierung – und zwar sichtbar.**
   *
   * Nils, 03.08.2026: *„Ich weiß gar nicht wie aktuell sortiert wird."* Er hatte recht:
   * die Liste übernahm still die Reihenfolge des Servers (`updated_at` absteigend), und im
   * Kopf stand nichts davon. Eine Tabelle, deren Reihenfolge man raten muss, ist eine
   * Tabelle, deren erster Zeile man nicht traut.
   *
   * Der Standard bleibt „zuletzt geändert, neueste zuerst" – aber er steht jetzt am
   * Spaltenkopf, und jede Spalte lässt sich drehen. Gemerkt wird beides, wie beim
   * Statusfilter: wer nach Alter sortiert, tut das meist wieder.
   */
  const [sortSpalte, setSortSpalte] = useMerker<SortSpalte>(
    'alle-sortspalte',
    'geaendert',
    SORT_SPALTEN,
  )
  const [sortAb, setSortAb] = useMerker<'ab' | 'auf'>('alle-sortrichtung', 'ab', ['ab', 'auf'])

  function sortierenNach(spalte: SortSpalte) {
    if (spalte === sortSpalte) {
      setSortAb(sortAb === 'ab' ? 'auf' : 'ab')
      return
    }
    setSortSpalte(spalte)
    /**
     * **Beim Spaltenwechsel die sinnvolle Richtung wählen**, nicht die zuletzt benutzte.
     * Wer auf „Alter" klickt, will die ältesten sehen; wer auf „Aufgabe" klickt, will A–Z.
     * Die alte Richtung beizubehalten hieße, dass der erste Klick fast immer der falsche ist.
     */
    setSortAb(spalte === 'titel' || spalte === 'bearbeiter' ? 'auf' : 'ab')
  }
  const [anlegenOffen, setAnlegenOffen] = useState(false)
  const [poolsFuer, setPoolsFuer] = useState<{ id: string; title: string } | null>(null)

  const quellen = useMemo(() => [...new Set(aufgaben.map((a) => a.sourceId))].sort(), [aufgaben])

  /**
   * Die Bereiche mit ihren Namen – **nach Menge sortiert, nicht alphabetisch.** Wer nach
   * einem Bereich filtert, sucht meist einen der großen; eine alphabetische Liste stellt
   * `epcanIOR` (67) vor `Entwicklung-Tickets` (1.234).
   */
  const bereiche = useMemo(() => {
    const karte = new Map<string, { label: string; anzahl: number }>()
    for (const a of aufgaben) {
      if (!a.scope) continue
      const vorhanden = karte.get(a.scope)
      if (vorhanden) vorhanden.anzahl++
      else karte.set(a.scope, { label: a.scopeLabel ?? a.scope, anzahl: 1 })
    }
    return [...karte.entries()]
      .map(([wert, { label, anzahl }]) => ({ wert, label: `${label} (${anzahl})`, anzahl }))
      .sort((x, y) => y.anzahl - x.anzahl)
  }, [aufgaben])

  const { data: vorhaben = [] } = useQuery(vorhabenQuery)
  const { data: meilensteine = [] } = useQuery(meilensteineQuery)

  /**
   * **Namen zu den Kennungen aus „kommen in Frage"** (24.09.2026).
   *
   * `tasks.all` liefert dort nur Kennungen – bewusst: Die Namen stehen im Client ohnehin
   * (`people.all`), und dieselbe Zeichenkette zweimal zu führen hieße, dass die zweite nach
   * einer Umbenennung veraltet. Dieselbe Überlegung wie bei den Schirmzeilen.
   */
  const { data: personen = [] } = useQuery(personenQuery)
  const namenNachId = useMemo(() => new Map(personen.map((p) => [p.id, p.displayName])), [personen])

  /**
   * **Der wirksame Termin an jeder Zeile** (16.09.2026, Nils mit einem Bild dieser Tabelle:
   * *„Und hier noch, dass ich auch das Zeitfenster (die errechnete Fälligkeit) sehe, falls
   * vorhanden."*).
   *
   * Dieselbe Ableitung wie in „Mein Tag", der Zeitachse und dem Trichter – `wirksamerTermin`:
   * Fälligkeit → Meilenstein → Zieltermin → Zeitfenster ab Anker ([E-281]). Hier ein zweites
   * Mal zu rechnen hieße, dass eine Aufgabe in dieser Tabelle ein anderes „bis wann" trüge als
   * im Vorlauf. Einmal über den Bestand je Render, wie dort.
   *
   * Der Termin hängt **vor** dem Filtern an der Zeile, damit die Sortierung ihn sieht.
   */
  const mitTermin = useMemo(() => {
    const jetzt = new Date()
    const vorhabenNachId = new Map<string, PlanungsVorhaben>(
      vorhaben.map((v) => [
        v.id,
        {
          id: v.id,
          zeitfenster: v.zeitfenster,
          zeitfensterAb: v.zeitfensterAb,
          zieltermin: v.zieltermin,
        },
      ]),
    )
    return aufgaben.map((a) => {
      const termin = wirksamerTermin(a, vorhabenNachId, meilensteine, jetzt)
      return {
        ...a,
        termin,
        faelligAm: termin?.datum ?? null,
        frist: fristMarke(termin, jetzt),
        /**
         * **Womit der Plan rechnet** (Z-19 AP 19.1) – dieselbe Funktion wie im Tagesplan und im
         * Aufgabendetail. Hier mitgerechnet statt in der Zelle, weil die Spalte **sortierbar**
         * ist: `vergleiche` bekommt nur die Zeile, nicht die Welt-Tafel dazu.
         */
        dauer: dauerAngabe(a, dauernJeWelt),
      }
    })
  }, [aufgaben, vorhaben, meilensteine, dauernJeWelt])

  /**
   * **Woher der Termin stammt** – als Tooltip, wie in „Mein Tag" ([E-308]). Die Fälligkeit
   * selbst wird nicht genannt: sie steht an der Aufgabe, wer sie gesetzt hat, sucht nicht nach ihr.
   */
  const terminHerkunft = (
    termin: Termin,
    aufgabe: { vorhabenId: string | null; meilensteinId: string | null },
  ): string | null => {
    if (termin === null || termin.quelle === 'faellig') return null
    if (termin.quelle === 'zieltermin') return 'Zieltermin des Vorhabens'
    if (termin.quelle === 'zeitfenster') return 'aus dem Zeitfenster'
    const bedient = meilensteinDerAufgabe(aufgabe, meilensteine)
    return bedient ? `Meilenstein „${bedient.label}"` : 'Meilenstein'
  }

  const gefiltert = useMemo(() => {
    const begriff = suche.trim().toLowerCase()
    return mitTermin.filter((a) => {
      // `isOpenStatus` aus dem geteilten Paket, damit „offen" hier dasselbe heißt
      // wie in der Auswertung und im WIP – nicht `!== 'erledigt'` an vier Stellen.
      if (statusFilter === 'offen' && !isOpenStatus(a.status)) return false
      if (statusFilter !== 'alle' && statusFilter !== 'offen' && a.status !== statusFilter) {
        return false
      }
      if (quelleFilter !== 'alle' && a.sourceId !== quelleFilter) return false
      if (bereichFilter !== 'alle' && a.scope !== bereichFilter) return false
      if (nurUneinig && !uneinig(a)) return false
      if (nurLiegengeblieben && !liegengeblieben(a)) return false
      if (nurVerwaist && !verwaist(a)) return false
      if (
        meinTagFilter !== 'alle' &&
        !passtZuMarkierFilter(markierungen.get(a.id) ?? null, heuteSchluessel(), meinTagFilter)
      )
        return false
      /**
       * **„ohne Vorhaben" heißt: offen und ohne Vorhaben** (Nils, 05.08.2026).
       *
       * *„Die erledigten Aufgaben müssen in kein Vorhaben mehr, die sind nur noch rein für
       * Auswertungszwecke im Leitstand wichtig."*
       *
       * Der Zähler am Knopf rechnete schon so; der Filter tat es nicht. Wer ihn anklickte,
       * bekam neben den 57 offenen über tausend erledigte dazu – und die Sammelzuordnung
       * darunter hätte sie alle mitgenommen.
       */
      if (vorhabenFilter === 'ohne' && (a.vorhabenId || !isOpenStatus(a.status))) return false
      if (vorhabenFilter !== 'alle' && vorhabenFilter !== 'ohne' && a.vorhabenId !== vorhabenFilter)
        return false
      if (!begriff) return true
      return (
        a.title.toLowerCase().includes(begriff) ||
        /**
         * **Alle Kennungen, nicht nur die führende.** Eine Aufgabe aus GitLab mit
         * NetCon-Ticket zeigt `GitLab !809`; wer nach `93551` sucht, meint dieselbe
         * Aufgabe. Bis zum 03.08.2026 fand er sie nicht – 760 Bezüge waren so unsichtbar.
         */
        (a.alleKennungen?.toLowerCase().includes(begriff) ?? false) ||
        (a.externalKey?.toLowerCase().includes(begriff) ?? false) ||
        (a.assigneeName?.toLowerCase().includes(begriff) ?? false)
      )
    })
  }, [
    mitTermin,
    suche,
    statusFilter,
    quelleFilter,
    bereichFilter,
    nurUneinig,
    nurLiegengeblieben,
    nurVerwaist,
    vorhabenFilter,
    meinTagFilter,
    markierungen,
  ])

  /** Über den **ganzen** Bestand gezählt, nicht über die gefilterte Sicht – sonst zeigte
   * der Knopf beim Filtern seine eigene Wirkung an. */
  const uneinigeGesamt = useMemo(() => aufgaben.filter(uneinig).length, [aufgaben])
  const liegengebliebenGesamt = useMemo(() => aufgaben.filter(liegengeblieben).length, [aufgaben])
  const verwaistGesamt = useMemo(() => aufgaben.filter(verwaist).length, [aufgaben])
  const ohneVorhaben = useMemo(
    () => aufgaben.filter((a) => !a.vorhabenId && isOpenStatus(a.status)).length,
    [aufgaben],
  )

  const sortiert = useMemo(() => {
    return [...gefiltert].sort((a, b) => vergleiche(a, b, sortSpalte, sortAb === 'ab'))
  }, [gefiltert, sortSpalte, sortAb])

  /**
   * Wo liegt welche Aufgabe – abgeleitet aus den geladenen Pools, ohne zweite
   * Abfrage. Zeigt die Pool-Symbole in der Spalte, damit man die Zuordnung sieht
   * statt sie zu erraten.
   */
  const poolsJeAufgabe = useMemo(() => {
    const karte = new Map<string, string>()
    for (const pool of pools) {
      for (const a of pool.aufgaben) {
        karte.set(a.id, (karte.get(a.id) ?? '') + pool.icon)
      }
    }
    return karte
  }, [pools])

  /**
   * Höhe einer Zeile. Geschätzt, nicht gemessen: alle Zeilen sind gleich hoch
   * (eine Textzeile plus Innenabstand), und `measureElement` wäre hier zusätzliche
   * Arbeit ohne Gegenwert. Weicht die Schätzung ab, verschiebt sich nur die
   * Bildlaufleiste leicht – der Inhalt bleibt richtig.
   */
  const ZEILENHOEHE = 37

  const scrollBereich = useRef<HTMLDivElement>(null)

  /**
   * **Häkchen an den Zeilen** (15.09.2026, Nils: *„Und die Sammelaktionen natürlich mit
   * Checkfeld – muss ja anhaken können."*).
   *
   * Der Filter bleibt die Vorauswahl; die Häkchen verengen sie. Ohne ein einziges Häkchen
   * gilt wie bisher alles, was in der Tabelle steht – so bleibt „72 zuordnen" ein Klick, und
   * wer nur drei davon meint, hakt drei an. Angehakt wird nur, was sichtbar ist: ein Häkchen
   * an einer Zeile, die der Filter gerade versteckt, zählt nicht mit, damit niemand unsichtbar
   * etwas erledigt.
   */
  const [gewaehlt, setGewaehlt] = useState<ReadonlySet<string>>(new Set())
  const auswahl = gefiltert.filter((a) => gewaehlt.has(a.id))
  const sammelZiel = auswahl.length > 0 ? auswahl : gefiltert
  const alleGewaehlt = gefiltert.length > 0 && auswahl.length === gefiltert.length
  const umschalten = (id: string) =>
    setGewaehlt((alt) => {
      const neu = new Set(alt)
      if (neu.has(id)) neu.delete(id)
      else neu.add(id)
      return neu
    })
  const alleUmschalten = () =>
    setGewaehlt(alleGewaehlt ? new Set() : new Set(gefiltert.map((a) => a.id)))
  const auswahlLeeren = () => setGewaehlt(new Set())

  const virtualisierer = useVirtualizer({
    count: sortiert.length,
    getScrollElement: () => scrollBereich.current,
    estimateSize: () => ZEILENHOEHE,
    /**
     * Zwölf Zeilen über den Rand hinaus. Weniger lässt beim schnellen Blättern
     * kurz Leere aufblitzen, mehr macht den Vorteil kleiner.
     */
    overscan: 12,
  })

  const fenster = virtualisierer.getVirtualItems()
  const platzOben = fenster.length > 0 ? (fenster[0]?.start ?? 0) : 0
  const platzUnten =
    fenster.length > 0 ? virtualisierer.getTotalSize() - (fenster.at(-1)?.end ?? 0) : 0

  return (
    <>
      <Topbar
        titel="Alle Aufgaben"
        untertitel={
          gefiltert.length === aufgaben.length
            ? `${aufgaben.length} Aufgaben`
            : `${gefiltert.length} von ${aufgaben.length}`
        }
        aktion={
          <Button variant="primary" onClick={() => setAnlegenOffen(true)}>
            + Neue Aufgabe
          </Button>
        }
      />

      {anlegenOffen && <AufgabeAnlegen schliessen={() => setAnlegenOffen(false)} />}

      {poolsFuer && (
        <PoolsSetzen
          aufgabeId={poolsFuer.id}
          titel={poolsFuer.title}
          schliessen={() => setPoolsFuer(null)}
        />
      )}

      <div className="mb-3.5 flex flex-wrap items-center gap-2">
        <input
          value={suche}
          onChange={(e) => setSuche(e.target.value)}
          placeholder="Titel, Ticketnummer oder Person …"
          className="w-[260px] rounded-[10px] border border-line2 bg-surface px-[13px] py-2 text-[13px] outline-none placeholder:text-muted"
        />
        <Auswahl
          wert={statusFilter}
          setzen={(v) => setStatusFilter(v as TaskStatus | 'alle' | 'offen')}
          optionen={[
            { wert: 'offen', label: 'Offen (ohne Erledigte)' },
            { wert: 'alle', label: 'Alle Stati' },
            ...TASK_STATUSES.map((s) => ({ wert: s, label: TASK_STATUS_LABELS[s] })),
          ]}
        />
        <Auswahl
          wert={quelleFilter}
          setzen={setQuelleFilter}
          optionen={[
            { wert: 'alle', label: 'Alle Quellen' },
            ...quellen.map((q) => ({
              wert: q,
              label: TASK_SOURCE_LABELS[q as TaskSource] ?? q,
            })),
          ]}
        />
        {/**
         * **Als Knopf mit Zahl, nicht als Auswahlpunkt.** Die Zahl ist die eigentliche
         * Auskunft: „7 uneinig" sagt, ob es sich lohnt hinzusehen, bevor man klickt. In
         * einem zugeklappten Menü stünde sie nicht.
         *
         * Und er erscheint nur, wenn es welche gibt – ein Filter, der immer null trifft,
         * ist eine Schaltfläche, die nichts tut.
         */}
        {uneinigeGesamt > 0 && (
          <button
            type="button"
            onClick={() => setNurUneinig((v) => !v)}
            aria-pressed={nurUneinig}
            className={`rounded-[10px] border px-3 py-2 text-[13px] ${
              nurUneinig
                ? 'border-pool-sofort bg-[#fdf0f0] text-pool-sofort'
                : 'border-line2 bg-surface hover:bg-[#fafaf8]'
            }`}
            title="Der Status passt nicht zusammen: eine Quelle sagt erledigt und die andere nicht, oder der Verlauf endet auf erledigt und die Aufgabe steht trotzdem offen, oder die Quelle ist zu und Work hält dagegen"
          >
            {nurUneinig ? '✓ ' : ''}
            {uneinigeGesamt} uneinig
          </button>
        )}

        {/**
         * **„ohne Vorhaben" steht oben**, nicht alphabetisch einsortiert: das ist die Frage,
         * mit der jemand hier hereinkommt.
         */}
        <Auswahl
          wert={vorhabenFilter}
          setzen={setVorhabenFilter}
          optionen={[
            { wert: 'alle', label: 'Alle Vorhaben' },
            { wert: 'ohne', label: `ohne Vorhaben (${ohneVorhaben})` },
            ...vorhaben.map((v) => ({ wert: v.id, label: v.label })),
          ]}
        />

        {liegengebliebenGesamt > 0 && (
          <button
            type="button"
            onClick={() => setNurLiegengeblieben((v) => !v)}
            aria-pressed={nurLiegengeblieben}
            className={`rounded-[10px] border px-3 py-2 text-[13px] ${
              nurLiegengeblieben
                ? 'border-[#8a6100] bg-[#fdf3dc] text-[#8a6100]'
                : 'border-line2 bg-surface hover:bg-[#fafaf8]'
            }`}
            title="Offen, aber seit über zwölf Monaten ist nichts passiert – kein Kommentar, kein Statuswechsel, nichts"
          >
            {nurLiegengeblieben ? '✓ ' : ''}
            {liegengebliebenGesamt} liegengeblieben
          </button>
        )}

        {/**
         * **Der Filter erscheint nur, wenn es welche gibt.** Ein Knopf mit „0 verwaist" wäre
         * eine Dauerfrage nach einem Zustand, den es nicht gibt – und stünde neben dem
         * Liegengebliebenen-Filter, der dasselbe Versprechen mit einer echten Zahl einlöst.
         */}
        {verwaistGesamt > 0 && (
          <button
            type="button"
            onClick={() => setNurVerwaist((v) => !v)}
            aria-pressed={nurVerwaist}
            className={`rounded-[10px] border px-3 py-2 text-[13px] ${
              nurVerwaist
                ? 'border-[#8a2f2f] bg-[#fdf0f0] text-[#8a2f2f]'
                : 'border-line2 bg-surface hover:bg-[#fafaf8]'
            }`}
            title="Offen, obwohl das übergeordnete Issue in der Quelle geschlossen ist – GitLab schließt Unteraufgaben nicht mit"
          >
            {nurVerwaist ? '✓ ' : ''}
            {verwaistGesamt} verwaist
          </button>
        )}

        {/**
         * **Mein Tag** (Z-20) – nur, wenn überhaupt etwas markiert ist. Gezählt über den ganzen
         * Bestand dieser Welt, nicht über die gefilterte Sicht – sonst zeigte der Knopf seine
         * eigene Wirkung an. „Ohne" bekommt keine Zahl: sie wäre fast der ganze Bestand.
         */}
        <MarkierFilterLeiste
          taskIds={aufgaben.map((a) => a.id)}
          wert={meinTagFilter}
          setzen={setMeinTagFilter}
        />

        {bereiche.length > 1 && (
          <Auswahl
            wert={bereichFilter}
            setzen={setBereichFilter}
            optionen={[
              { wert: 'alle', label: 'Alle Bereiche' },
              ...bereiche.map(({ wert, label }) => ({ wert, label })),
            ]}
          />
        )}

        {/**
         * **Wonach sortiert wird, steht da – auch wenn die Spalte nicht sichtbar ist.**
         *
         * Der Standard „zuletzt geändert" hat in der Tabelle **keine** Spalte; ohne diese
         * Zeile wäre die Reihenfolge beim Öffnen unerklärlich. Genau das war Nils'
         * Beobachtung: *„Ich weiß gar nicht wie aktuell sortiert wird."*
         *
         * Als Auswahl und nicht als bloßer Text, weil „zuletzt geändert" sonst der einzige
         * Sortierschlüssel wäre, den man nicht wählen kann – die Spalte, die ihn trägt, gibt
         * es ja nicht.
         */}
        <Auswahl
          wert={sortSpalte}
          setzen={(v) => sortierenNach(v as SortSpalte)}
          optionen={SORT_SPALTEN.map((sp) => ({ wert: sp, label: `Sortiert: ${SORT_LABELS[sp]}` }))}
        />
        <button
          type="button"
          onClick={() => setSortAb(sortAb === 'ab' ? 'auf' : 'ab')}
          className="rounded-[10px] border border-line2 bg-surface px-3 py-2 text-[13px] hover:bg-[#fafaf8]"
          title={
            sortAb ? 'Absteigend – klicken für aufsteigend' : 'Aufsteigend – klicken für absteigend'
          }
        >
          {sortAb ? '▾ absteigend' : '▴ aufsteigend'}
        </button>
      </div>

      <SammelZuordnung
        sichtbar={sammelZiel}
        vorhaben={vorhaben}
        ausgewaehlt={auswahl.length > 0}
        fertig={auswahlLeeren}
      />
      <SammelStatus sichtbar={sammelZiel} ausgewaehlt={auswahl.length > 0} fertig={auswahlLeeren} />
      {/**
       * **Für Mein Tag – für die Auswahl** (Z-20). Nur mit ausgewählten Zeilen, nicht für „alle
       * sichtbaren": dreihundert Aufgaben auf morgen zu legen ist kein Plan, sondern ein Versehen.
       */}
      {darfMarkieren && auswahl.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 text-[12.5px]">
          <span className="text-ink2">
            ☀ {auswahl.length} {auswahl.length === 1 ? 'Aufgabe' : 'Aufgaben'} für Mein Tag:
          </span>
          <MarkierKnoepfe taskIds={auswahl.map((a) => a.id)} />
        </div>
      )}
      {nurVerwaist && <SammelErledigen sichtbar={sammelZiel.filter(verwaist)} />}

      {isLoading ? (
        <Ladehinweis />
      ) : (
        <Card className="overflow-hidden">
          {/*
            Der Bildlauf gehört **in** die Karte, nicht ans Fenster: der
            Virtualisierer braucht ein Element mit fester Höhe, an dem er die
            Bildlaufposition ablesen kann. `70vh` lässt Kopfzeile und Filter stehen.
          */}
          {/*
            `overflow-auto` statt `overflow-y-auto` und eine Mindestbreite an der Tabelle
            ([Z-10](../../../../docs/phasen/zusatz-10-mobil.md)): acht Spalten mit festen
            Breiten ergeben zusammen rund 820 px. Auf einem Telefon würde `table-fixed` sie
            trotzdem auf 390 px zusammenquetschen – acht unlesbare Streifen. Sie scrollen
            jetzt lieber **in der Karte** seitwärts, statt die ganze Seite mitzunehmen.
          */}
          <div ref={scrollBereich} className="max-h-[70vh] overflow-auto">
            <table className="w-full min-w-[1430px] table-fixed border-collapse text-[12.5px]">
              <colgroup>
                {/*
                  **Feste Breiten – und sie müssen vollzählig sein** (siehe Kopfkommentar: sonst
                  springen die Spalten beim Blättern).

                  Am 24.09.2026 kam „Kommen in Frage" dazu, **ohne** eine Breite hier – zehn
                  Angaben für elf Spalten. `table-fixed` verteilt den Rest dann selbst, und die
                  Tabelle sah aus wie zerschoben: „bis 06.10. · 10 Tage" brach auf drei Zeilen,
                  Namen auf zwei. Wer hier eine Spalte ergänzt, ergänzt auch eine Zeile hier.
                */}
                <col className="w-[36px]" />
                <col />
                <col className="w-[136px]" />
                <col className="w-[92px]" />
                <col className="w-[52px]" />
                <col className="w-[152px]" />
                <col className="w-[164px]" />
                <col className="w-[116px]" />
                <col className="w-[138px]" />
                {/* Z-20: „Mein Tag" neben „Fällig" – Absicht und Zusage nebeneinander. */}
                <col className="w-[112px]" />
                <col className="w-[62px]" />
                <col className="w-[88px]" />
              </colgroup>
              {/*
                `top-0`, nicht `unter-leiste` (17.09.2026): der Kopf klebt am Rand **dieses**
                Scroll-Bereichs, nicht am Bildschirm. Mit 48 px Abstand liefen darüber die
                Zeilen durch, sobald man in der Tabelle scrollte.
              */}
              <thead className="sticky top-0 z-10 bg-surface">
                <tr>
                  <th className="border-b border-line2 px-2.5 py-2 text-left">
                    <input
                      type="checkbox"
                      aria-label={alleGewaehlt ? 'Auswahl aufheben' : 'Alle sichtbaren auswählen'}
                      checked={alleGewaehlt}
                      ref={(el) => {
                        if (el) el.indeterminate = auswahl.length > 0 && !alleGewaehlt
                      }}
                      onChange={alleUmschalten}
                      className="size-4 cursor-pointer accent-accent"
                    />
                  </th>
                  {SPALTEN.map(({ titel, sortiereNach }) => {
                    const aktiv = sortiereNach !== null && sortiereNach === sortSpalte
                    return (
                      <th
                        key={titel}
                        aria-sort={aktiv ? (sortAb ? 'descending' : 'ascending') : 'none'}
                        className="border-b border-line2 px-2.5 py-2 text-left text-[10.5px] font-semibold tracking-[0.5px] text-muted uppercase"
                      >
                        {sortiereNach === null ? (
                          titel
                        ) : (
                          /**
                           * **Ein Knopf, kein `onClick` am `th`.** Sortieren muss mit der
                           * Tastatur gehen (Leitplanke 3), und ein Tabellenkopf ist für
                           * eine Bildschirmlesehilfe keine Schaltfläche.
                           */
                          <button
                            type="button"
                            onClick={() => sortierenNach(sortiereNach)}
                            className={`-mx-1 flex items-center gap-1 rounded px-1 uppercase hover:text-ink2 ${
                              aktiv ? 'text-ink' : ''
                            }`}
                            /**
                             * **`aria-label`, nicht nur `title`.** Vorgelesen wird sonst
                             * „Alter ▾" – der sichtbare Text ist die Spalte, aber die
                             * Handlung ist das Sortieren. Der `title` bleibt für die Maus.
                             */
                            aria-label={`Nach ${titel} sortieren`}
                            title={`Nach ${titel} sortieren`}
                          >
                            {titel}
                            {/* Der Pfeil steht nur an der aktiven Spalte – sonst wäre er Zierde. */}
                            {aktiv && <span aria-hidden="true">{sortAb ? '▾' : '▴'}</span>}
                          </button>
                        )}
                      </th>
                    )
                  })}
                </tr>
              </thead>
              <tbody>
                {platzOben > 0 && <tr aria-hidden="true" style={{ height: platzOben }} />}
                {fenster
                  .map(({ index }) => sortiert[index])
                  .map((aufgabe) =>
                    !aufgabe ? null : (
                      <tr
                        key={aufgabe.id}
                        className={
                          gewaehlt.has(aufgabe.id) ? 'bg-accent-soft/40' : 'hover:bg-[#fafaf8]'
                        }
                      >
                        <td className="border-b border-line px-2.5 py-[9px]">
                          <input
                            type="checkbox"
                            aria-label={`${aufgabe.title} auswählen`}
                            checked={gewaehlt.has(aufgabe.id)}
                            onChange={() => umschalten(aufgabe.id)}
                            className="size-4 cursor-pointer accent-accent"
                          />
                        </td>
                        <td className="border-b border-line px-2.5 py-[9px] font-semibold break-words">
                          <AufgabenVerweis
                            aufgabeId={aufgabe.id}
                            className="cursor-pointer text-left hover:underline"
                          >
                            {aufgabe.title}
                          </AufgabenVerweis>
                        </td>
                        <td className="border-b border-line px-2.5 py-[9px]">
                          <SourceBadge
                            source={aufgabe.sourceId as TaskSource}
                            label={aufgabe.externalKey}
                          />
                          {/**
                           * **Die Handlungsanweisung steht in der Zeile**, nicht erst im
                           * Detail: `NetCon #93551 (offen)` sagt, was zu tun ist – und wo.
                           */}
                          {aufgabe.quellenUneinig === 1 && aufgabe.offeneQuelle && (
                            <span
                              className="ml-1.5 rounded-md bg-[#fdf0f0] px-1.5 py-px text-[10px] font-semibold text-pool-sofort"
                              title="Diese Quelle sagt noch nicht erledigt"
                            >
                              {aufgabe.offeneQuelle}
                            </span>
                          )}
                          {/**
                           * **Der zweite Grund, und er braucht eigene Worte.**
                           *
                           * „NetCon #93551 (bereit)" sagt: *geh in die andere Quelle und
                           * schließ sie dort.* Hier ist es umgekehrt – die Quelle **hat**
                           * geschlossen und ist danach ohne Vermerk wieder aufgegangen. Zu
                           * tun ist etwas an **dieser** Aufgabe, nicht an einer zweiten
                           * Quelle. Dieselbe Beschriftung für beides wäre die falsche
                           * Handlungsanweisung an der halben Liste.
                           */}
                          {aufgabe.verlaufUneinig === 1 && (
                            <span
                              className="ml-1.5 rounded-md bg-[#fdf0f0] px-1.5 py-px text-[10px] font-semibold text-pool-sofort"
                              title="Der Verlauf endet auf ‚erledigt‘, die Aufgabe steht trotzdem offen – die Quelle wurde ohne Vermerk wieder aufgemacht"
                            >
                              Verlauf sagt erledigt
                            </span>
                          )}
                          {/**
                           * **Der dritte Grund, und hier ist Work der Grund** (24.08.2026,
                           * [E-215](../../../../docs/ENTSCHEIDUNGEN.md)).
                           *
                           * Die Quelle ist zu, Work sagt etwas anderes. Zu tun ist nichts in
                           * der Quelle – dort stimmt alles –, sondern **hier**: entweder die
                           * Aufgabe in Work abschließen oder sie bewusst wieder aufnehmen.
                           * „Quelle ist zu" statt „uneinig" sagt genau das.
                           */}
                          {aufgabe.abschlussGehalten === 1 && (
                            <span
                              className="ml-1.5 rounded-md bg-[#fdf0f0] px-1.5 py-px text-[10px] font-semibold text-pool-sofort"
                              title="Die Quelle ist erledigt, Work hält dagegen – Work macht den Abschluss nicht von selbst wieder auf. Hier entscheiden: abschließen oder bewusst wieder aufnehmen."
                            >
                              Quelle ist zu
                            </span>
                          )}
                          {/**
                           * **Warum diese Zeile eine Leiche ist, steht in der Zeile.**
                           *
                           * Ohne die Kennung *`!294`* wäre „verwaist" eine Behauptung, die man
                           * nur im Filter nachzählen kann. Mit ihr ist sie in der Quelle
                           * nachprüfbar – und man sieht auf einen Blick, dass sechs Zeilen
                           * dasselbe Elternteil haben.
                           */}
                          {verwaist(aufgabe) && (
                            <span
                              className="ml-1.5 rounded-md bg-[#fdf0f0] px-1.5 py-px text-[10px] font-semibold text-pool-sofort"
                              title={elternHinweis(aufgabe)}
                            >
                              Eltern {elternKennung(aufgabe)} zu
                            </span>
                          )}
                        </td>
                        <td className="border-b border-line px-2.5 py-[9px] whitespace-nowrap text-ink2">
                          {TASK_STATUS_LABELS[aufgabe.status]}
                        </td>
                        <td className="border-b border-line px-2.5 py-[9px]">
                          {aufgabe.priority ? <PrioBadge priority={aufgabe.priority} /> : '–'}
                        </td>
                        <td className="border-b border-line px-2.5 py-[9px] text-ink2">
                          {/**
                           * **Namen als Marke** (24.09.2026, Nils: *„Die namen eher als tags
                           * auch."*). In einer Tabelle mit elf Spalten trennt eine Fläche zwei
                           * Namen schneller als ein Komma – und sie zeigt auf einen Blick, wo
                           * jemand steht und wo nur ein Strich.
                           */}
                          {aufgabe.assigneeName ? (
                            <Chip>{aufgabe.assigneeName}</Chip>
                          ) : (
                            <span className="text-muted">–</span>
                          )}
                        </td>
                        <td className="border-b border-line px-2.5 py-[9px] text-ink2">
                          {/**
                           * **Wer dafür in Frage kommt** – Wunsch an der Aufgabe, ersatzweise am
                           * Vorhaben, dazu die Bearbeiter aus der Quelle. Genau die Liste, die
                           * auch im Aufgabendetail unter „Kommen in Frage" steht; gerechnet wird
                           * sie einmal im Server (`kommenInFrageIds`), nicht hier noch einmal.
                           *
                           * Wer schon Bearbeiter ist, steht nicht noch einmal daneben: In der
                           * Zeile davor ist es beantwortet, und eine Wiederholung liest sich wie
                           * eine zweite Aussage.
                           */}
                          {(() => {
                            /**
                             * **Eine kommagetrennte Zeichenkette, keine Liste.** Der Ausdruck im
                             * Server (`kommenInFrageIdsNurOffen`) setzt die Kennungen mit
                             * `GROUP_CONCAT` zusammen; `Zeitachse.tsx` formt genauso um.
                             */
                            const namen = String(aufgabe.wunschIds ?? '')
                              .split(',')
                              .filter(Boolean)
                              .filter((id) => id !== aufgabe.assigneeId)
                              .map((id) => namenNachId.get(id))
                              .filter((n): n is string => Boolean(n))
                            if (namen.length === 0) return <span className="text-muted">–</span>
                            return (
                              <span
                                className="flex flex-wrap items-center gap-1"
                                title={namen.join(', ')}
                              >
                                {namen.slice(0, 2).map((n) => (
                                  <Chip key={n}>{n}</Chip>
                                ))}
                                {namen.length > 2 && (
                                  <span className="text-[11px] text-muted">
                                    + {namen.length - 2}
                                  </span>
                                )}
                              </span>
                            )
                          })()}
                        </td>
                        <td className="border-b border-line px-2.5 py-[9px] text-ink2">
                          {aufgabe.vorhabenLabel ?? <span className="text-muted">–</span>}
                        </td>
                        <td className="border-b border-line px-2.5 py-[9px] text-ink2">
                          {/**
                           * **Frist und Einstufung wie im Vorlauf** – „in 3 Tagen · 10 T",
                           * überfällig rot. Ohne Termin ein Strich wie in den Nachbarspalten,
                           * nicht „ohne Frist": in einer Tabelle ist das Fehlen die Regel.
                           */}
                          {aufgabe.termin ? (
                            <Fristwort
                              frist={aufgabe.frist}
                              zeitfenster={aufgabe.zeitfenster}
                              herkunft={terminHerkunft(aufgabe.termin, aufgabe)}
                            />
                          ) : (
                            <span className="text-muted">–</span>
                          )}
                        </td>
                        <td className="border-b border-line px-2.5 py-[7px]">
                          {isOpenStatus(aufgabe.status) ? (
                            <MarkierAuswahl taskId={aufgabe.id} titel={aufgabe.title} />
                          ) : (
                            <span className="text-muted">–</span>
                          )}
                        </td>
                        <td
                          className="tabular border-b border-line px-2.5 py-[9px] whitespace-nowrap text-ink2"
                          title={dauerHerkunftText(aufgabe.dauer.herkunft)}
                        >
                          {dauerText(aufgabe.dauer.minuten)}
                          {/**
                           * **Ohne Größe ist die Zahl geraten** – und das muss man sehen, nicht
                           * nur im Tooltip. [F-93](../../../../docs/OFFENE-FRAGEN.md): 78 offene
                           * Aufgaben haben keine Größe; eine Spalte, in der bei allen dieselben
                           * 15 Minuten stehen, als wäre das eine Auskunft, wäre die schlechtere
                           * Anzeige als gar keine.
                           */}
                          {aufgabe.dauer.herkunft === 'vorgabe' && (
                            <span className="ml-1 text-[10.5px] text-muted">?</span>
                          )}
                        </td>
                        <td className="tabular border-b border-line px-2.5 py-[9px] whitespace-nowrap text-ink2">
                          {alter(aufgabe)}
                          {/**
                           * **Wie lange nichts passiert ist** – aber nur, wenn es auffällig
                           * ist. Bei jeder Zeile eine zweite Zahl wäre Lärm; bei den 43
                           * Liegengebliebenen ist sie die eigentliche Auskunft.
                           *
                           * Steht neben dem Alter und nicht in einer eigenen Spalte, weil sie
                           * dieselbe Frage beantwortet: *wie lange geht das schon so?*
                           */}
                          {liegengeblieben(aufgabe) && (
                            <span
                              className="ml-1.5 rounded px-1 text-[10.5px] font-semibold text-[#8a6100]"
                              title={
                                aufgabe.letztesEreignisAm
                                  ? `Zuletzt passiert am ${new Date(aufgabe.letztesEreignisAm).toLocaleDateString('de-DE')}`
                                  : 'Nie ein Ereignis verzeichnet'
                              }
                            >
                              {aufgabe.letztesEreignisAm
                                ? `${monateSeit(aufgabe.letztesEreignisAm)} M still`
                                : 'nie'}
                            </span>
                          )}
                        </td>
                        <td className="border-b border-line px-2.5 py-[9px]">
                          {/*
                      Auch an importierten Aufgaben erlaubt: Pool-Zugehörigkeit
                      gibt es im Quellsystem nicht, ein GitLab-Issue in einen
                      Work-Pool zu legen ändert dort nichts (F-17).
                    */}
                          <button
                            type="button"
                            onClick={() => setPoolsFuer({ id: aufgabe.id, title: aufgabe.title })}
                            className="cursor-pointer rounded-md px-1.5 py-0.5 text-[11px] font-semibold text-accent-deep hover:bg-accent-soft"
                            aria-label={`Pools für ${aufgabe.title} setzen`}
                          >
                            {poolsJeAufgabe.get(aufgabe.id) ?? 'einsortieren'}
                          </button>
                        </td>
                      </tr>
                    ),
                  )}
                {platzUnten > 0 && <tr aria-hidden="true" style={{ height: platzUnten }} />}
                {sortiert.length === 0 && (
                  <tr>
                    <td colSpan={SPALTEN.length + 1} className="px-2.5 py-8 text-center text-muted">
                      Keine Aufgabe passt zu den Filtern.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </>
  )
}

function Auswahl({
  wert,
  setzen,
  optionen,
}: {
  wert: string
  setzen: (wert: string) => void
  optionen: { wert: string; label: string }[]
}) {
  return (
    <select
      value={wert}
      onChange={(e) => setzen(e.target.value)}
      className="rounded-[10px] border border-line2 bg-surface px-3 py-2 text-[13px] text-ink2 outline-none"
    >
      {optionen.map((o) => (
        <option key={o.wert} value={o.wert}>
          {o.label}
        </option>
      ))}
    </select>
  )
}

/**
 * Alter ab dem Zeitpunkt, an dem es die Aufgabe gibt.
 *
 * Welcher das ist, entscheidet `entstanden` im geteilten Paket – die Quellzeit, und
 * bei einer in Work angelegten Aufgabe ihr Anlagedatum. Hier stand bis zum
 * 04.08.2026 nur `sourceCreatedAt`, und die eigenen Aufgaben zeigten „–".
 */
function alter(aufgabe: { sourceCreatedAt: Date | string | null; createdAt: Date | string }) {
  const tage = Math.floor((Date.now() - entstanden(aufgabe).getTime()) / 86_400_000)
  if (tage <= 0) return 'heute'
  if (tage === 1) return '1 Tag'
  return `${tage} Tage`
}

/* ── Sortierung ─────────────────────────────────────────────────────── */

export const SORT_SPALTEN = [
  'titel',
  'quelle',
  'status',
  'prio',
  'bearbeiter',
  'alter',
  'geaendert',
  'vorhaben',
  'faellig',
  'dauer',
] as const
export type SortSpalte = (typeof SORT_SPALTEN)[number]

/**
 * Die Spalten der Tabelle und wonach sie sortieren.
 *
 * `Pools` sortiert nicht: eine Aufgabe kann in mehreren liegen, und „nach Pools sortiert"
 * hätte keine eindeutige Bedeutung. Ein Kopf, der sich anklicken lässt und dann etwas
 * Willkürliches tut, ist schlechter als einer, der es nicht tut.
 */
const SPALTEN: { titel: string; sortiereNach: SortSpalte | null }[] = [
  { titel: 'Aufgabe', sortiereNach: 'titel' },
  { titel: 'Quelle', sortiereNach: 'quelle' },
  { titel: 'Status', sortiereNach: 'status' },
  { titel: 'Prio', sortiereNach: 'prio' },
  { titel: 'Bearbeiter', sortiereNach: 'bearbeiter' },
  /**
   * **„Kommen in Frage" neben dem Bearbeiter** (24.09.2026, von Nils beauftragt: *„kannst du
   * hier die «Kommen in Frage» Leute in einer Spalte ergänzen das ich die sehe?"*).
   *
   * Die beiden Spalten beantworten zwei verschiedene Fragen, die nebeneinander gehören: *wer
   * macht es* und *wer könnte es machen*. Eine Aufgabe ohne Bearbeiter, aber mit Vorgemerkten,
   * ist etwas anderes als eine, die niemand im Blick hat – genau der Unterschied, den Nils
   * schon am Eingang sehen wollte ([E-131](../../../../docs/ENTSCHEIDUNGEN.md)).
   *
   * **Sortiert nicht** – wie „Pools" und aus demselben Grund: Es können mehrere sein, und
   * „nach Kommen in Frage sortiert" hätte keine eindeutige Bedeutung. Ein Kopf, der sich
   * anklicken lässt und dann etwas Willkürliches tut, ist schlechter als einer, der es nicht tut.
   */
  { titel: 'Kommen in Frage', sortiereNach: null },
  { titel: 'Vorhaben', sortiereNach: 'vorhaben' },
  { titel: 'Fällig', sortiereNach: 'faellig' },
  /**
   * **„Mein Tag" direkt neben „Fällig"** ([Z-20](../../../../docs/phasen/zusatz-20-mein-tag-markieren.md)).
   * Nebeneinander, weil man genau diese beiden vergleicht: *wann muss es fertig sein* und *wann
   * will ich daran*. Sortiert nicht – gefiltert wird über die Knöpfe oben, und eine Sortierung
   * nach einer privaten Angabe hätte für alle Zeilen ohne sie keine Ordnung.
   */
  { titel: 'Mein Tag', sortiereNach: null },
  /**
   * **„Dauer" – womit der Plan rechnet** ([Z-19](../../../../docs/phasen/zusatz-19-tagesplan.md)
   * AP 19.1). Die Zahl gab es seit E-373, sichtbar war sie nur als Blocklänge im Tagesplan –
   * also dort, wo man sie nicht nachprüfen kann.
   *
   * **Sortierbar**, anders als „Pools" und „Kommen in Frage": Die Dauer ist je Aufgabe genau
   * eine Zahl, und *„was geht schnell?"* ist eine Frage, die man an diese Tabelle stellt.
   */
  { titel: 'Dauer', sortiereNach: 'dauer' },
  { titel: 'Alter', sortiereNach: 'alter' },
  { titel: 'Pools', sortiereNach: null },
]

/** Reihenfolge des Ablaufs, nicht des Alphabets – „bereit" kommt vor „in Arbeit". */
const STATUS_RANG: Record<string, number> = {
  eingang: 0,
  bereit: 1,
  in_arbeit: 2,
  review: 3,
  erledigt: 4,
  zurueckgestellt: 5,
}

export type Zeile = {
  title: string
  sourceId: string
  status: string
  priority: string | null
  assigneeName: string | null
  sourceCreatedAt: string | Date | null
  /** Anlagedatum in Work – ausgewertet nur zusammen mit `sourceCreatedAt` über `entstanden`. */
  createdAt: string | Date
  updatedAt: string | Date | null
  /** Phase 8 – für die Spalte „Vorhaben" und ihre Sortierung. */
  vorhabenLabel?: string | null
  /** Der wirksame Termin (16.09.2026) – `null` heißt „keiner", und die landen hinten. */
  faelligAm?: Date | null
  /**
   * **Die geplante Dauer in Minuten** (Z-19 AP 19.1) – gerechnet, nicht gespeichert.
   *
   * Sie ist **nie leer**: Ohne Schätzung und ohne Größe gilt die Vorgabe. Deshalb braucht sie
   * in der Sortierung auch keine Sonderbehandlung für fehlende Werte.
   */
  dauer?: { minuten: number }
}

/**
 * Vergleich für die Sortierung. **Fehlende Werte landen immer hinten**, unabhängig von der
 * Richtung.
 *
 * Das ist die Stelle, an der eine naive Sortierung falsch wird: bei „Prio absteigend"
 * stünden sonst die 112 Aufgaben **ohne** Priorität ganz oben – und genau die sucht dort
 * niemand. Wer nach einer Spalte sortiert, will die Zeilen sehen, die in dieser Spalte
 * etwas stehen haben.
 */
export function vergleiche(a: Zeile, b: Zeile, spalte: SortSpalte, ab = false): number {
  /**
   * **Die Richtung gehört hier hinein, nicht nach draußen.**
   *
   * Zuerst drehte der Aufrufer das Ergebnis um (`richtung * vergleiche(…)`) – und damit
   * wanderten die leeren Werte bei *aufsteigend* nach vorn. Ein Test hat es gefunden: bei
   * „Priorität aufsteigend" standen die 112 Aufgaben **ohne** Priorität ganz oben, also
   * genau die, die dort niemand sucht.
   *
   * Leere Werte dürfen von der Richtung **nicht** betroffen sein. Das geht nur, wenn eine
   * Stelle beides kennt.
   */
  const richtung = ab ? -1 : 1

  /** `null` heißt „beide haben einen Wert" – dann entscheidet der Vergleich darunter. */
  const leerZuletzt = (x: unknown, y: unknown) => {
    const xLeer = x === null || x === undefined || x === ''
    const yLeer = y === null || y === undefined || y === ''
    if (xLeer && yLeer) return 0
    if (xLeer) return 1
    if (yLeer) return -1
    return null
  }

  switch (spalte) {
    case 'titel':
      return richtung * a.title.localeCompare(b.title, 'de')
    case 'quelle':
      return richtung * a.sourceId.localeCompare(b.sourceId, 'de')
    case 'status':
      // Nach dem Ablauf, nicht nach dem Alphabet: „bereit" kommt vor „in Arbeit".
      return richtung * ((STATUS_RANG[a.status] ?? 9) - (STATUS_RANG[b.status] ?? 9))
    case 'prio': {
      const vor = leerZuletzt(a.priority, b.priority)
      if (vor !== null) return vor
      // A ist wichtiger als C – absteigend sortiert heißt hier „A zuerst".
      return richtung * String(b.priority).localeCompare(String(a.priority), 'de')
    }
    case 'bearbeiter': {
      const vor = leerZuletzt(a.assigneeName, b.assigneeName)
      if (vor !== null) return vor
      return richtung * String(a.assigneeName).localeCompare(String(b.assigneeName), 'de')
    }
    case 'vorhaben': {
      /** Ohne Vorhaben ans Ende – das ist die Arbeitsliste, nicht der Anfang des Alphabets. */
      const vor = leerZuletzt(a.vorhabenLabel, b.vorhabenLabel)
      if (vor !== null) return vor
      return (a.vorhabenLabel ?? '').localeCompare(b.vorhabenLabel ?? '', 'de')
    }
    case 'dauer': {
      /** Immer eine Zahl – `0` nur, wenn die Zeile aus einem Test ohne Dauer kommt. */
      return richtung * ((a.dauer?.minuten ?? 0) - (b.dauer?.minuten ?? 0))
    }
    case 'faellig': {
      /** Ohne Termin ans Ende – in beide Richtungen; aufsteigend heißt „das Nächste zuerst". */
      const vor = leerZuletzt(a.faelligAm, b.faelligAm)
      if (vor !== null) return vor
      return richtung * ((a.faelligAm as Date).getTime() - (b.faelligAm as Date).getTime())
    }
    case 'alter': {
      /**
       * **„Alter aufsteigend" heißt: kleinstes Alter zuerst**, also das jüngste Datum.
       * Absteigend stehen die ältesten oben – und dort ist die Zahl der Tage am größten,
       * so wie es in der Spalte steht.
       *
       * Hier stand zuerst ein zusätzliches `* -1`; damit tat die Spalte das Gegenteil
       * dessen, was am Kopf steht. Zwei Verneinungen in einer Zeile sind eine zu viel.
       *
       * **Kein `leerZuletzt` mehr:** ein Alter hat jede Aufgabe – `entstanden` fällt auf
       * das Anlagedatum zurück. Vorher landeten die in Work angelegten Aufgaben in beiden
       * Richtungen am Ende, obwohl sie die jüngsten sind.
       */
      return richtung * (entstanden(b).getTime() - entstanden(a).getTime())
    }
    case 'geaendert': {
      const vor = leerZuletzt(a.updatedAt, b.updatedAt)
      if (vor !== null) return vor
      // Aufsteigend: die am längsten unberührten zuerst. Standard ist absteigend.
      return (
        richtung *
        (new Date(a.updatedAt as string).getTime() - new Date(b.updatedAt as string).getTime())
      )
    }
  }
}

/**
 * Beschriftungen der Sortierschlüssel – **in der Sprache der Frage, nicht der Spalte.**
 *
 * „Alter" heißt in der Tabelle die Zahl der Tage; in der Auswahl steht „Alter (älteste
 * zuerst)", weil dort die Frage lautet „was will ich sehen" und nicht „wie heißt die
 * Spalte".
 */
const SORT_LABELS: Record<SortSpalte, string> = {
  geaendert: 'zuletzt geändert',
  alter: 'Alter',
  faellig: 'Fälligkeit',
  vorhaben: 'Vorhaben',
  titel: 'Titel',
  quelle: 'Quelle',
  status: 'Status',
  prio: 'Priorität',
  bearbeiter: 'Bearbeiter',
  dauer: 'Dauer',
}

/**
 * **Liegengeblieben: offen, aber seit über zwölf Monaten ohne jedes Ereignis** (Phase 7).
 *
 * ## Warum das Ereignis-Log und nicht `updatedAt`
 *
 * `updatedAt` ändert sich bei **jedem Import**, auch wenn die Quelle nur dasselbe noch einmal
 * geliefert hat. Danach zu gehen hieße: eine Aufgabe, die seit zwei Jahren niemand anfasst,
 * sieht taufrisch aus, weil der Abgleich alle 15 Minuten läuft.
 *
 * Gefragt ist, wann zuletzt jemand **gehandelt** hat – ein Kommentar, ein Statuswechsel, eine
 * Zuweisung. Das steht im Log.
 *
 * ## Warum zwölf Monate
 *
 * Gemessen am 03.08.2026 am offenen Bestand:
 *
 * ```
 * unter 3 Monate     39
 * 3–12 Monate        56
 * 1–2 Jahre          53   ← davon 36 ohne Bearbeiter
 * ```
 *
 * Die Grenze bei zwölf Monaten trifft **43 von 150**. Bei sechs Monaten wären es über die
 * Hälfte – und ein Filter, der die halbe Liste markiert, markiert nichts.
 *
 * ## Warum das kein Vorschlag zum Schließen ist
 *
 * Weil „lange nichts passiert" nicht „unwichtig" heißt. Die Phasendatei nennt die
 * Relevanz-Bewertung *„liegt seit 14 Monaten, Melder hat gekündigt → noch wichtig?"* – die
 * zweite Hälfte davon weiß Work nicht. **Sichtbar machen kommt vor Entscheiden**, und
 * entschieden wird beim Durchgehen, nicht von einer Schwelle.
 */
const ZWOELF_MONATE_MS = 365 * 24 * 60 * 60 * 1000

function liegengeblieben(a: { status: string; letztesEreignisAm?: string | null }): boolean {
  if (!isOpenStatus(a.status as TaskStatus)) return false
  /** Ohne jedes Ereignis ist erst recht nichts passiert. */
  if (!a.letztesEreignisAm) return true
  return Date.now() - new Date(a.letztesEreignisAm).getTime() > ZWOELF_MONATE_MS
}

/**
 * **Uneinig: der Status ist zweifelhaft, sieh nach** – aus einem von drei Gründen.
 *
 * | Grund | wer widerspricht wem | Beispiel |
 * |---|---|---|
 * | `quellenUneinig` | zwei Quellen an derselben Aufgabe | GitLab erledigt, NetCon offen |
 * | `verlaufUneinig` | **eine** Quelle ihrem eigenen Verlauf | NetCon #37166 |
 * | `abschlussGehalten` | **Work** einem Abschluss in der Quelle | GitLab !880 |
 *
 * Zusammen in einer Funktion, weil sie zusammen einen Arbeitsvorrat ergeben: Nils geht die
 * Liste durch und entscheidet je Zeile. Getrennte Knöpfe wären drei Durchgänge durch
 * dieselbe Frage.
 *
 * Getrennt bleiben sie in der **Begründung** an der Zeile – „NetCon #93551 (bereit)" ist eine
 * andere Handlungsanweisung als „Verlauf sagt erledigt".
 *
 * **Der dritte ist seit dem 24.08.2026 dabei** ([E-215](../../../../docs/ENTSCHEIDUNGEN.md)) und
 * unterscheidet sich von den beiden anderen: hier ist nicht eine Quelle unklar, sondern Work
 * hält dagegen. Vorher hat der Rückschreiber diese Lage aufgelöst, indem er den Abschluss in
 * GitLab wieder aufmachte. Das tut er nicht mehr – dafür muss die Zeile hier stehen.
 */
function uneinig(a: {
  quellenUneinig?: number | null
  verlaufUneinig?: number | null
  abschlussGehalten?: number | null
}): boolean {
  return a.quellenUneinig === 1 || a.verlaufUneinig === 1 || a.abschlussGehalten === 1
}

/** Volle Monate seit einem Zeitpunkt – für „14 M still" neben dem Alter. */
function monateSeit(wann: string): number {
  return Math.floor((Date.now() - new Date(wann).getTime()) / (30 * 24 * 60 * 60 * 1000))
}

/**
 * **Verwaist: offen, obwohl das übergeordnete Issue geschlossen ist** (05.08.2026).
 *
 * GitLab schließt Unteraufgaben nicht mit dem Elternteil. Wer das Elternteil zumacht, hält
 * die Sache für erledigt – die Kinder bleiben offen, tauchen in keiner Liste des Elternteils
 * mehr auf und altern still vor sich hin.
 *
 * **Der Zustand des Elternteils, nicht sein Alter.** Eine Frist gehört hier nicht hinein:
 * gemessen war das jüngste geschlossene Elternteil 216 Tage alt, das älteste 525. Eine
 * Wartezeit hätte in diesem Bestand nichts unterschieden und wäre eine erfundene Zahl.
 */
function verwaist(a: { status: string; parentState?: string | null }): boolean {
  if (!isOpenStatus(a.status as TaskStatus)) return false
  return a.parentState === 'closed'
}

/** `epcan-main/netcon/web/NetConWeb!294` → `!294`. Die Nummer ist, was in GitLab steht. */
function elternKennung(a: { parentExternalId?: string | null }): string {
  const iid = a.parentExternalId?.split('!').pop()
  return iid ? `!${iid}` : '–'
}

function elternHinweis(a: {
  parentExternalId?: string | null
  parentTitle?: string | null
  parentClosedAt?: string | Date | null
}): string {
  const titel = a.parentTitle ? `„${a.parentTitle}"` : 'Übergeordnetes Issue'
  const zu = a.parentClosedAt
    ? `geschlossen am ${new Date(a.parentClosedAt).toLocaleDateString('de-DE')}`
    : 'geschlossen (Datum unbekannt)'
  return `${titel} ${elternKennung(a)} ist ${zu} – diese Aufgabe ist offen geblieben`
}

/**
 * **Alles zuordnen, was der Filter gerade zeigt** (Phase 8).
 *
 * ## Warum über den Filter und nicht über Häkchen
 *
 * Bei 150 offenen Aufgaben ist eine Mehrfachauswahl mit Häkchen eine Übung in Ausdauer. Die
 * Filter dieser Ansicht können schon genau das, was man beim Zuordnen braucht: *„alles aus
 * Bereich NetConWeb, was noch kein Vorhaben hat"*. Was dann in der Tabelle steht, **ist** die
 * Auswahl – man sieht sie, bevor man sie bestätigt, statt sie zusammenzuklicken.
 *
 * ## Warum die Zahl am Knopf steht
 *
 * Dieselbe Überlegung wie beim Probelauf der Triage-Regeln und bei der Sammel-Übernahme: wer
 * einen Stapel bestätigt, muss vorher wissen, wie groß er ist.
 */
/**
 * **Die Waisen in einem Zug erledigen** (Phase 7, 05.08.2026).
 *
 * ## Warum eine Sammelaktion und kein Hintergrunddienst
 *
 * Nils' erster Vorschlag war ein Dienst, der vier Tage nach dem Schließen des Elternteils
 * aufräumt. Die Messung sprach dagegen: alle 12 Fälle liegen zwischen **216 und 525 Tagen**
 * – ein Dienst mit Frist hätte am ersten Tag alles auf einmal geschlossen und danach jahrelang
 * leergedreht. Das ist keine Automatik, das ist ein einmaliger Aufräumlauf mit Wartungsvertrag.
 *
 * Und die Reihenfolge steht schon fest: **Sichtbar machen kommt vor Entscheiden**
 * ([E-73](../../../../docs/ENTSCHEIDUNGEN.md)). Was der Filter zeigt, ist die Auswahl – man
 * sieht sie, bevor man sie bestätigt.
 *
 * ## Warum „rückgängig" direkt danebensteht
 *
 * Die Liste in „Eingang" trägt es auch, aber wer hier gerade zwölf Aufgaben erledigt hat,
 * steht hier – und nicht dort.
 */
function SammelErledigen({ sichtbar }: { sichtbar: { id: string }[] }) {
  const queryClient = useQueryClient()
  const [bericht, setBericht] = useState<{ text: string; aktionId?: string } | null>(null)

  const auffrischen = async () => {
    await queryClient.invalidateQueries({ queryKey: ['tasks'] })
    await queryClient.invalidateQueries({ queryKey: ['eingang'] })
  }

  const erledigen = useMutation({
    mutationFn: () => trpc.tasks.verwaisteErledigen.mutate({ taskIds: sichtbar.map((a) => a.id) }),
    onSuccess: async (b) => {
      setBericht({
        text:
          `${b.erledigt} erledigt.` +
          /**
           * Übersprungene werden **benannt**, nicht verschwiegen: sie entstehen, wenn der
           * Bestand im Browser älter ist als der auf dem Server – und dann ist die
           * Differenz die eigentliche Auskunft.
           */
          (b.uebersprungen ? ` ${b.uebersprungen} übersprungen (nicht mehr verwaist).` : ''),
        ...(b.aktionId ? { aktionId: b.aktionId } : {}),
      })
      await auffrischen()
    },
  })

  const zurueck = useMutation({
    mutationFn: (id: string) => trpc.eingang.massenaktionZurueck.mutate({ id }),
    onSuccess: async (b) => {
      setBericht({
        text: b.ok ? `${b.zurueckgesetzt} zurückgenommen.` : (b.grund ?? 'Ging nicht.'),
      })
      await auffrischen()
    },
  })

  if (sichtbar.length === 0) return null

  return (
    <div className="mb-3 flex flex-wrap items-center gap-2 rounded-[10px] border border-[#e8c9c9] bg-[#fdf7f7] px-3 py-2 text-[12.5px]">
      <span className="text-ink2">
        Diese {sichtbar.length} {sichtbar.length === 1 ? 'Aufgabe hängt' : 'Aufgaben hängen'} unter
        einem geschlossenen Issue:
      </span>
      <Button
        variant="primary"
        size="sm"
        disabled={erledigen.isPending}
        onClick={() => erledigen.mutate()}
      >
        {sichtbar.length} erledigen
      </Button>
      {bericht && <span className="text-ink2">{bericht.text}</span>}
      {bericht?.aktionId && (
        <Button
          variant="ghost"
          size="sm"
          disabled={zurueck.isPending}
          onClick={() => zurueck.mutate(bericht.aktionId as string)}
        >
          rückgängig
        </Button>
      )}
      {/**
       * **Was in GitLab passiert, steht dran** – vor dem Klick, nicht danach. Wer „erledigen"
       * drückt, erwartet sonst, dass das Issue drüben zugeht (F-23: Work labelt, es schließt
       * nicht).
       */}
      <span className="ml-auto text-[11px] text-muted">
        Setzt in GitLab „Status::Erledigt“ – geschlossen wird das Issue dort nicht.
      </span>
    </div>
  )
}

/**
 * **Erledigt oder zurückgestellt – für alles, was der Filter gerade zeigt** (15.09.2026).
 *
 * Nils, nach dem ersten Outlook-Abgleich mit 72 alten Kennzeichnungen: *„da bräuchte ich wohl
 * noch eine Sammelfunktion, dass ich da «Ignorieren» oder Erledigt machen kann."* Dieselbe
 * Überlegung wie bei der Zuordnung: der Filter **ist** die Auswahl, die Zahl steht am Knopf,
 * und die Rücknahme ist ein Klick daneben. „Zurückstellen" ist das Ignorieren: die Aufgabe
 * bleibt sichtbar, zählt nirgends mit und schreibt nichts nach Outlook zurück – „Erledigt"
 * dagegen setzt dort das Häkchen.
 *
 * Eine Rückfrage vor dem Klick, weil es hier um Dutzende geht und der Knopf neben dem
 * Zuordnen steht.
 *
 * ## Und „Zusammenführen" für genau zwei Häkchen (16.09.2026)
 *
 * Nils, mit zwei gleichnamigen Zeilen im Bild (dieselbe Mail einmal über das Work-Postfach,
 * einmal aus dem eigenen Outlook): *„und hier müsste ich noch «Zusammenführen» als Knopf
 * haben."* Bisher ging das nur aus dem Aufgabendetail heraus, mit Suche nach der zweiten. Hier
 * stehen beide schon nebeneinander – der Knopf öffnet dasselbe Fenster ([Z-11], `AufgabeZusammenfuehren`)
 * mit beiden vorgewählt und beginnt bei der Vorschau. Es **bleibt** die obere der beiden
 * Zeilen; „andersherum" tauscht. Der Knopf steht nur mit Häkchen, denn „alles, was der Filter
 * zeigt" ist keine sinnvolle Menge für ein Verschmelzen – und er ist gesperrt, solange es nicht
 * genau zwei sind.
 */
function SammelStatus({
  sichtbar,
  ausgewaehlt = false,
  fertig,
}: {
  sichtbar: { id: string; title: string }[]
  /** Kommt die Menge aus den Häkchen (statt aus dem Filter)? Ändert nur den Wortlaut. */
  ausgewaehlt?: boolean
  /** Nach einer Aktion: die Häkchen leeren, damit sie nicht auf der nächsten Aktion liegen. */
  fertig?: () => void
}) {
  const queryClient = useQueryClient()
  const [bericht, setBericht] = useState<{ text: string; aktionId?: string } | null>(null)
  const [verschmelzen, setVerschmelzen] = useState(false)

  const auffrischen = async () => {
    await queryClient.invalidateQueries({ queryKey: ['tasks'] })
    await queryClient.invalidateQueries({ queryKey: ['eingang'] })
  }

  const setzen = useMutation({
    mutationFn: (status: 'erledigt' | 'zurueckgestellt') =>
      trpc.tasks.massenStatus.mutate({ taskIds: sichtbar.map((a) => a.id), status }),
    onSuccess: async (b, status) => {
      setBericht({
        text:
          `${b.geaendert} ${status === 'erledigt' ? 'erledigt' : 'zurückgestellt'}.` +
          (b.uebersprungen ? ` ${b.uebersprungen} übersprungen.` : ''),
        ...(b.aktionId ? { aktionId: b.aktionId } : {}),
      })
      fertig?.()
      await auffrischen()
    },
  })

  const zurueck = useMutation({
    mutationFn: (id: string) => trpc.eingang.massenaktionZurueck.mutate({ id }),
    onSuccess: async (b) => {
      setBericht({
        text: b.ok ? `${b.zurueckgesetzt} zurückgenommen.` : (b.grund ?? 'Ging nicht.'),
      })
      await auffrischen()
    },
  })

  if (sichtbar.length < 1 || (!ausgewaehlt && sichtbar.length < 2)) return null

  /** Die beiden für „Zusammenführen" – nur gesetzt, wenn es genau zwei sind. */
  const [erste, zweite] = sichtbar.length === 2 ? sichtbar : [undefined, undefined]

  const fragen = (status: 'erledigt' | 'zurueckgestellt') => {
    const wort = status === 'erledigt' ? 'auf Erledigt setzen' : 'zurückstellen'
    if (
      window.confirm(`Diese ${sichtbar.length} Aufgaben ${wort}? Lässt sich danach zurücknehmen.`)
    ) {
      setzen.mutate(status)
    }
  }

  return (
    <div className="mb-3 flex flex-wrap items-center gap-2 rounded-[10px] border border-line bg-surface px-3 py-2 text-[12.5px]">
      <span className="text-muted">
        {ausgewaehlt ? `${sichtbar.length} angehakt:` : `Diese ${sichtbar.length} Aufgaben:`}
      </span>
      <Button
        variant="ghost"
        size="sm"
        disabled={setzen.isPending}
        onClick={() => fragen('erledigt')}
        title="Setzt alle sichtbaren auf Erledigt – bei Outlook und To Do wird daraus das Häkchen."
      >
        ✓ {sichtbar.length} erledigt
      </Button>
      <Button
        variant="ghost"
        size="sm"
        disabled={setzen.isPending}
        onClick={() => fragen('zurueckgestellt')}
        title="Ignorieren: sichtbar, zählt nirgends mit, schreibt nichts zurück."
      >
        {sichtbar.length} zurückstellen
      </Button>
      {ausgewaehlt && (
        <Button
          variant="ghost"
          size="sm"
          disabled={sichtbar.length !== 2}
          onClick={() => setVerschmelzen(true)}
          title={
            sichtbar.length === 2
              ? 'Aus den zwei angehakten Aufgaben wird eine – mit Vorschau, was mitzieht.'
              : 'Genau zwei Aufgaben anhaken, dann lassen sie sich zusammenführen.'
          }
        >
          Zusammenführen
        </Button>
      )}
      {verschmelzen && erste && zweite && (
        <AufgabeZusammenfuehren
          aufgabeId={erste.id}
          titel={erste.title}
          partnerVorgewaehlt={zweite.id}
          schliessen={() => setVerschmelzen(false)}
          fertig={() => {
            setVerschmelzen(false)
            setBericht({ text: 'Zusammengeführt – aus zwei Aufgaben wurde eine.' })
            fertig?.()
          }}
        />
      )}
      {bericht && <span className="text-ink2">{bericht.text}</span>}
      {bericht?.aktionId && (
        <Button
          variant="ghost"
          size="sm"
          disabled={zurueck.isPending}
          onClick={() => zurueck.mutate(bericht.aktionId as string)}
        >
          rückgängig
        </Button>
      )}
      <span className="ml-auto text-[11px] text-muted">
        {ausgewaehlt
          ? 'Betrifft die angehakten Zeilen.'
          : 'Betrifft genau das, was gerade in der Tabelle steht – oder hak einzelne an.'}
      </span>
    </div>
  )
}

function SammelZuordnung({
  sichtbar,
  vorhaben,
  ausgewaehlt = false,
  fertig,
}: {
  sichtbar: { id: string }[]
  vorhaben: { id: string; label: string }[]
  ausgewaehlt?: boolean
  fertig?: () => void
}) {
  const queryClient = useQueryClient()
  const [ziel, setZiel] = useState('')
  const [bericht, setBericht] = useState<string | null>(null)

  const zuordnen = useMutation({
    mutationFn: (vorhabenId: string | null) =>
      trpc.vorhaben.aufgabenZuordnen.mutate({ taskIds: sichtbar.map((a) => a.id), vorhabenId }),
    onSuccess: async (b) => {
      setBericht(`${b.zugeordnet} zugeordnet.`)
      fertig?.()
      await nebenBestandVerwerfen(queryClient)
      await queryClient.invalidateQueries({ queryKey: ['vorhaben'] })
    },
  })

  /** Ohne Vorhaben gäbe es nichts zu wählen, und bei einer Aufgabe tut es das Detail. */
  if (vorhaben.length === 0 || sichtbar.length < 1 || (!ausgewaehlt && sichtbar.length < 2)) {
    return null
  }

  return (
    <div className="mb-3 flex flex-wrap items-center gap-2 rounded-[10px] border border-line bg-surface px-3 py-2 text-[12.5px]">
      <span className="text-muted">
        {ausgewaehlt
          ? `${sichtbar.length} angehakt zuordnen:`
          : `Diese ${sichtbar.length} Aufgaben zuordnen:`}
      </span>
      <select
        className="rounded-md border border-line px-2 py-1 text-[12px]"
        value={ziel}
        onChange={(e) => {
          setZiel(e.target.value)
          setBericht(null)
        }}
      >
        <option value="">– Vorhaben wählen –</option>
        {vorhaben.map((v) => (
          <option key={v.id} value={v.id}>
            {v.label}
          </option>
        ))}
        <option value="__keins">– Zuordnung lösen –</option>
      </select>
      <Button
        variant="primary"
        size="sm"
        disabled={!ziel || zuordnen.isPending}
        onClick={() => zuordnen.mutate(ziel === '__keins' ? null : ziel)}
      >
        {sichtbar.length} zuordnen
      </Button>
      {bericht && <span className="text-ink2">{bericht}</span>}
      <span className="ml-auto text-[11px] text-muted">
        Betrifft genau das, was gerade in der Tabelle steht.
      </span>
    </div>
  )
}
